import { FC, ReactNode } from 'react';
import { Avatar, Dropdown, Input, Menu } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { LearnMoreFooter } from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { getSchemaFieldIcon } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/schemaFieldIcon';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import SelectOptionContent from '../../../../components/SelectOptionContent';
import { useDatasetSchema } from '../../../01-Overview/hooks';

interface GroupedByDropdownProps {
    component: ReportComponent;
    children: ReactNode;
    slot?: number;
}

const GroupedByDropdown: FC<GroupedByDropdownProps> = ({ children, component, slot = 0 }): JSX.Element => {
    const { token } = useAntDesignTheme();

    const updateDraft = useReportComponentStore((s) => s.updateDraftConfiguration);
    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);

    const draft: Partial<ReportComponentConfig> = draftConfigurations[component?.id as string];

    const { schema } = useDatasetSchema(draft);

    const metrics = schema?.dimensions ?? [];

    const menu = (
        <DropdownMenuPanel style={{ marginTop: '0.5rem', maxHeight: '25rem' }}>
            <div style={{ borderBottom: `1px solid ${token.colorBorder}` }}>
                <Input bordered={false} placeholder='Filter Options...' />
            </div>
            <div style={{ margin: '0.325rem 0', padding: '0 0.5rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Grouped By Options</small>
            </div>
            {metrics.map((m) => {
                return (
                    <Menu.Item
                        key={m.key}
                        onClick={() => {
                            const dimensionsDraft = [...(draft?.query?.dimensions ?? [])];
                            dimensionsDraft[slot] = {
                                field: m.key,
                                label: m.label,
                            };

                            const updatedDraft = {
                                ...draft,
                                query: {
                                    ...draft?.query,
                                    datasetId: draft.query?.datasetId ?? '',
                                    metrics: draft.query?.metrics ?? [],
                                    dimensions: dimensionsDraft,
                                },
                            };

                            updateDraft(component.id, updatedDraft);
                        }}
                    >
                        <SelectOptionContent
                            icon={
                                <Avatar
                                    size={16}
                                    shape='square'
                                    icon={getSchemaFieldIcon(m, token.colorTextTertiary, 'dimension')}
                                    style={{ border: `1px solid ${token.colorBorder}` }}
                                />
                            }
                            title={m.label}
                            description={m.type}
                        />
                    </Menu.Item>
                );
            })}
            <LearnMoreFooter topic='charting functionality' variant='dropdown' />
        </DropdownMenuPanel>
    );

    return (
        <Dropdown trigger={['click']} popupRender={() => menu}>
            <span>{children}</span>
        </Dropdown>
    );
};

export default GroupedByDropdown;
